import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { ProductSelectorComponent } from '../../common/product-selector/product-selector.component';
import { OrdersFacade } from '../../../+store/order/order.facade';
import {
  IOrderLineItem,
  IProductSubscriptionPhase,
  IProductV2,
  IProductV2Option
} from '@care-portals/api-interfaces';
import { takeWhile } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { ProductFacade } from '../../../+store/product/product.facade';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { INSURANCE_TYPES } from '@care-portals/data';
import { Subscription } from 'rxjs';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { getPhaseByCycle } from '@care-portals/shared';

@Component({
  selector: 'portals-emr-order-create-v2',
  templateUrl: './create-order-v2.component.html',
  styleUrls: ['./create-order-v2.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ProductSelectorComponent,
    NzLayoutModule,
    NzSelectModule,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    NzTagModule,
    NzDividerModule,
    NzInputNumberModule,
    NzCheckboxModule
  ]
})
export class OrderCreateV2Component implements OnInit, OnDestroy {
  @ViewChild(ProductSelectorComponent, { static: true })
  productSelector: ProductSelectorComponent;

  @ViewChild('basicTable') basicTable: any;

  @Input() mode: 'create' | 'refill';
  @Input() customerId: string;
  @Input() products: IOrderLineItem[];

  isLoading = false;
  isLoadingCategories = false;
  isLoadingProducts = false;
  categories = [];
  category = 0;
  orderForm!: FormGroup;
  error;
  active = true;
  insuranceTypes = INSURANCE_TYPES;
  isDraftOrder = false;
  isSubmitting = false;
  sendEvent = true;
  private subscriptions: Subscription[] = [];

  constructor(
    private notification: NzNotificationService,
    private facade: OrdersFacade,
    private productFacade: ProductFacade,
    private formBuilder: FormBuilder,
    private nzDrawerRef: NzDrawerRef
  ) {
    this.orderForm = this.formBuilder.group({
      items: new FormArray([])
    });

    this.facade.loading$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoading = res));

    this.productFacade.loading$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoadingProducts = res));

    this.productFacade.loadingCategories$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoadingCategories = res));
    this.productFacade.categories$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.categories = [{ id: 0, name: 'All' }, ...res]));

    this.facade.orderResult$
      .pipe(takeWhile(() => this.active))
      .subscribe(orderResult => {
        if (orderResult.success) {
          this.isSubmitting = false;
          this.nzDrawerRef.close(orderResult.data.id);
          this.facade.clearOrderResult();
        }
      });

    this.facade.error$.pipe(takeWhile(() => this.active)).subscribe(res => {
      this.error = res;
      if (res) {
        this.isSubmitting = false;
      }
    });
  }

  get items(): FormArray {
    return this.orderForm.get('items') as FormArray;
  }

  get total(): number {
    return this.items.controls.reduce((acc, item) => {
      return acc + this.calculateSubTotal(item as FormGroup);
    }, 0);
  }

  get insuranceCoverage(): number {
    return this.items.controls.reduce((acc, item) => {
      return acc + this.calculateInsuranceCoverage(item as FormGroup);
    }, 0);
  }

  calculateSubTotal(item: FormGroup): number {
    const quantity = item.get('quantity')?.value || 0;
    const salePrice = item.get('salePrice')?.value || 0;
    const discount = item.get('discount')?.value || 0;
    const isSubscription = item.get('isSubscription')?.value;
    const subscriptionPhases = item.get('subscriptionPhases')?.value as
      | IProductSubscriptionPhase[]
      | null
      | undefined;
    const insuranceCoverage = this.calculateInsuranceCoverage(item);
    const effectiveUnitPrice = isSubscription
      ? (this.getSubscriptionPriceForCycle(
        subscriptionPhases,
        quantity || 1
      ) ?? salePrice)
      : salePrice;
    const effectiveQuantity = isSubscription ? 1 : quantity;
    const totalPrice = effectiveQuantity * effectiveUnitPrice;

    return Math.round((totalPrice - discount - insuranceCoverage) * 100) / 100;
  }

  calculateInsuranceCoverage(item: FormGroup): number {
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;
    return (
      Math.round(
        insuranceCoverages.controls.reduce((total, insurance) => {
          return total + (insurance.get('amount')?.value || 0);
        }, 0) * 100
      ) / 100
    );
  }

  // Form validators
  private decimalValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const value = control.value.toString();
    const decimalPlaces = value.includes('.') ? value.split('.')[1].length : 0;
    return decimalPlaces > 2 ? { tooManyDecimals: true } : null;
  }

  private drugPriceValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;

    const salePrice = parent.get('salePrice')?.value || 0;
    const quantity = parent.get('quantity')?.value || 0;
    const totalPrice = salePrice * quantity;

    if (control.value > totalPrice) {
      return { drugPriceExceedsSalePrice: true };
    }
    return null;
  }

  private discountValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;

    const salePrice = parent.get('salePrice')?.value || 0;
    const quantity = parent.get('quantity')?.value || 0;
    const drugPrice = parent.get('drugPrice')?.value || 0;
    const totalPrice = salePrice * quantity;
    const maxDiscount = totalPrice - drugPrice;

    if (control.value > maxDiscount) {
      return { discountExceedsMax: true };
    }
    return null;
  }

  private subscriptionQuantityValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;

    const isSubscription = parent.get('isSubscription')?.value;
    if (isSubscription && control.value > 1) {
      return { subscriptionQuantityExceeded: true };
    }
    return null;
  }

  ngOnInit() {
    this.productFacade.loadCategories();
    this.productFacade.loadProducts();

    if (this.mode === 'refill') {
      this.products.forEach(product => {
        // Fetch full product details to get complete options with choices
        this.productFacade.getProductV2(product.productId.toString());

        // Subscribe to the selected product to get the full details
        this.productFacade.selectedProduct$
          .pipe(takeWhile(() => this.active))
          .subscribe(fullProduct => {
            if (
              fullProduct &&
              fullProduct._id === product.productId.toString()
            ) {
              // Map the existing options to include choices from the full product
              const optionsWithChoices =
                fullProduct.options
                  ?.map(fullOption => {
                    if (product.options[fullOption.type]) {
                      return {
                        type: fullOption.type,
                        choices: (fullOption?.choices || []).map(choice => ({
                          ...choice,
                          selected:
                            choice.value === product.options[fullOption.type]
                        }))
                      };
                    }
                    return null;
                  })
                  .filter(Boolean)
                  .map(option => option) || [];

              this.addProduct(
                product.name,
                '' + product.productId,
                product.sku,
                product.salePrice,
                product.listPrice,
                product.quantity,
                optionsWithChoices,
                product.isSubscription || false,
                [],
                product.visitFee || 0,
                product.shippingFee || 0
              );
            }
          });
      });
    }
  }

  ngOnDestroy(): void {
    this.active = false;
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  onProductSelected(product: IProductV2): void {
    this.addProduct(
      product.label,
      '' + product._id,
      product.sku,
      product.price,
      product.listPrice,
      1,
      product.options,
      product.isSubscription,
      product.subscriptionPhases,
      product.visitFee || 0,
      product.shippingFee || 0
    );
  }

  // Add input handler for decimal restriction
  onDecimalInput(event: any, control: AbstractControl) {
    const value = event.target.value;
    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1].length > 2) {
        control.setValue(Number(parts[0] + '.' + parts[1].substring(0, 2)));
      }
    }
  }

  private updateInsuranceAmountValidity(item: FormGroup) {
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;
    insuranceCoverages.controls.forEach(insuranceCtrl => {
      insuranceCtrl.get('amount')?.updateValueAndValidity({ onlySelf: true });
    });
  }

  addProduct(
    name: string,
    productId: string,
    sku: string,
    price: number,
    listPrice: number,
    quantity: number = 1,
    options: IProductV2Option[] = [],
    isSubscription: boolean = false,
    subscriptionPhases: IProductSubscriptionPhase[] = [],
    visitFee: number = 0,
    shippingFee: number = 0
  ) {
    const existProduct = this.items?.value.filter(
      item => item.productId === productId
    );
    if (existProduct.length > 0) {
      this.notification.info('Information', 'Already exist.');
      return;
    }

    const optionsControls = options.map(option =>
      this.formBuilder.group({
        type: [option.type],
        value: [
          option.choices?.find(c => c.selected)?.value ||
          option.choices?.find(c => c.default)?.value ||
          option.choices?.[0]?.value ||
          ''
        ],
        label: [
          option.choices?.find(c => c.selected)?.label ||
          option.choices?.find(c => c.default)?.label ||
          option.choices?.[0]?.label ||
          ''
        ],
        choices: [option.choices]
      })
    );

    const fgs = this.formBuilder.group({
      name: [name],
      productId: ['' + productId],
      sku: [sku],
      salePrice: [price || 0, [Validators.required, this.decimalValidator]],
      listPrice: [listPrice || 0],
      quantity: [
        quantity,
        [
          Validators.required,
          Validators.min(1),
          (control: AbstractControl) => {
            const phases =
              (control.parent?.get('subscriptionPhases')?.value as
                | IProductSubscriptionPhase[]
                | undefined) ?? subscriptionPhases;
            if (!phases?.length) return null;

            const lastPhase = phases[phases.length - 1];
            const isInfiniteCycles = lastPhase.cycles === 0;
            if (isInfiniteCycles) return null;

            const totalCycleLimit = phases.reduce(
              (sum, phase) => sum + (phase.cycles || 0),
              0
            );

            const value = Number(control.value);
            if (value > totalCycleLimit) {
              return {
                maxCycles: { max: totalCycleLimit, actual: value }
              };
            }
            return null;
          }
        ]
      ],
      options: this.formBuilder.array(optionsControls),
      isSubscription: [isSubscription],
      subscriptionPhases: [subscriptionPhases],
      drugPrice: [0, [this.decimalValidator, this.drugPriceValidator]],
      discount: [0, [this.decimalValidator, this.discountValidator]],
      visitFee: [visitFee || 0, [this.decimalValidator, Validators.min(0)]],
      shippingFee: [
        shippingFee || 0,
        [this.decimalValidator, Validators.min(0)]
      ],
      insuranceCoverages: this.formBuilder.array([])
    });

    // Set up cross-field validation
    const salePriceControl = fgs.get('salePrice');
    const quantityControl = fgs.get('quantity');
    const drugPriceControl = fgs.get('drugPrice');
    const discountControl = fgs.get('discount');

    // Update validations when price or quantity changes
    this.subscriptions.push(
      salePriceControl?.valueChanges.subscribe(() => {
        drugPriceControl?.updateValueAndValidity();
        discountControl?.updateValueAndValidity();
        this.updateInsuranceAmountValidity(fgs);
      })
    );

    this.subscriptions.push(
      quantityControl?.valueChanges.subscribe(() => {
        if (fgs.get('isSubscription')?.value) {
          const phases = fgs.get('subscriptionPhases')?.value as IProductSubscriptionPhase[];
          const cycle = quantityControl?.value ?? 1;
          const cyclePrice = this.getSubscriptionPriceForCycle(phases, cycle);
          if (cyclePrice != null) {
            fgs.get('salePrice')?.setValue(cyclePrice, { emitEvent: false });
          }
        }
        drugPriceControl?.updateValueAndValidity();
        discountControl?.updateValueAndValidity();
        this.updateInsuranceAmountValidity(fgs);
      })
    );

    // Re-validate quantity when subscription phases change (so max cycle limit updates)
    this.subscriptions.push(
      fgs.get('subscriptionPhases')?.valueChanges.subscribe(() => {
        quantityControl?.updateValueAndValidity();
      })
    );

    // Update validations when drugPrice changes
    this.subscriptions.push(
      drugPriceControl?.valueChanges.subscribe(() => {
        discountControl?.updateValueAndValidity();
        this.validateAllInsuranceAmounts(fgs);
      })
    );

    this.orderForm.setControl(
      'items',
      new FormArray([...this.items.controls, fgs])
    );
  }

  private validateAllInsuranceAmounts(item: FormGroup): void {
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;
    insuranceCoverages.controls.forEach(insuranceCtrl => {
      insuranceCtrl.get('amount')?.updateValueAndValidity({ emitEvent: true });
    });
  }

  removeProduct(index: number): void {
    this.items.removeAt(index);
    if (this.basicTable) {
      this.basicTable.data = [...this.items.controls];
    }
  }

  clearProducts(): void {
    this.orderForm?.setControl('items', new FormArray([]));
  }

  submit(): void {
    if (this.isSubmitting) return;

    if (!this.orderForm.valid) {
      this.notification.error(
        'Error',
        'Please fix all validation errors before submitting.'
      );
      return;
    }

    this.isSubmitting = true;

    const orderItemsDTO: IOrderLineItem[] = this.items?.controls.map(
      itemCtrl => {
        const itemValue = itemCtrl.value;
        const discount = itemValue.discount || 0;
        const drugPrice = itemValue.drugPrice || 0;

        return {
          name: itemValue.name,
          productId: itemValue.productId || 0,
          sku: itemValue.sku,
          salePrice: itemValue.salePrice || 0,
          quantity: itemValue.quantity,
          options: (itemCtrl.get('options') as FormArray).controls.reduce(
            (acc, optionCtrl) => {
              acc[optionCtrl.get('type').value] = optionCtrl.get('value').value;
              return acc;
            },
            {} as { [type: string]: string }
          ),
          drugPrice: drugPrice > 0 ? drugPrice : undefined,
          visitFee: itemValue.visitFee || 0,
          shippingFee: itemValue.shippingFee || 0,
          discount:
            discount > 0
              ? {
                type: 'manual',
                amount: discount,
                reductionAmount: discount,
                reductionType: 'dollar'
              }
              : undefined,
          insuranceCoverages: (
            itemCtrl.get('insuranceCoverages') as FormArray
          ).controls.map(insuranceCtrl => ({
            type: insuranceCtrl.get('type').value,
            amount: insuranceCtrl.get('amount').value
          })),
          totalInsuranceAmount:
            Math.round(
              (itemCtrl.get('insuranceCoverages') as FormArray).controls.reduce(
                (acc, insuranceCtrl) => {
                  return acc + insuranceCtrl.get('amount').value;
                },
                0
              ) * 100
            ) / 100
        } as IOrderLineItem;
      }
    );

    this.facade.createOrder(
      orderItemsDTO,
      this.customerId,
      this.mode === 'refill',
      this.isDraftOrder,
      this.sendEvent
    );
  }

  getSubscriptionPriceForCycle(
    phases: IProductSubscriptionPhase[] | null | undefined,
    cycle: number
  ): number | null {
    if (!phases?.length || cycle < 1) return null;
    const currentPhase = getPhaseByCycle(phases, cycle);
    return currentPhase?.price ?? null;
  }

  lastPhase(subscriptionPhases: IProductSubscriptionPhase[]): string {
    const lastIndex = subscriptionPhases?.length - 1;
    if (!lastIndex && lastIndex !== 0) return '';
    const lastPhase = subscriptionPhases[lastIndex];
    return `$${lastPhase.price}, Every ${lastPhase.fillingCycleInterval} ${lastPhase.fillingCycleUnit}`;
  }

  isInsuranceMaxed(itemIndex: number): boolean {
    const item = this.items.at(itemIndex) as FormGroup;
    if (!item) return false;
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;
    if (insuranceCoverages.length === 0) return false;

    const drugPrice = item.get('drugPrice').value;
    const quantity = item.get('quantity').value;
    const totalPrice = drugPrice * quantity;
    const currentInsuranceTotal = this.calculateInsuranceCoverage(item);
    return currentInsuranceTotal > totalPrice;
  }

  addInsurance(itemIndex: number): void {
    const item = this.items.at(itemIndex) as FormGroup;
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;

    insuranceCoverages.push(
      this.formBuilder.group({
        type: ['other', Validators.required],
        amount: [
          0,
          [
            Validators.required,
            Validators.min(0),
            this.decimalValidator,
            () => {
              const drugPrice = item.get('drugPrice').value;
              const quantity = item.get('quantity').value;
              const totalPrice = drugPrice * quantity;
              const currentInsuranceTotal =
                this.calculateInsuranceCoverage(item);
              return currentInsuranceTotal > totalPrice
                ? { insuranceExceedsPrice: true }
                : null;
            }
          ]
        ]
      })
    );
    this.updateInsuranceAmountValidity(item);
  }

  removeInsurance(itemIndex: number, insuranceIndex: number): void {
    const item = this.items.at(itemIndex);
    const insuranceCoverages = item.get('insuranceCoverages') as FormArray;
    insuranceCoverages.removeAt(insuranceIndex);
    this.updateInsuranceAmountValidity(item as FormGroup);
  }
}
